import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { useCreateInvite } from "@/hooks/mutations/use-create-invite";
import { useGetGroups } from "@/hooks/queries/use-get-groups";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

const InviteSchema = z.object({
	groups: z.array(z.string()).optional(),
	type: z.enum(["internal", "external"]),
});

type InviteFormType = z.infer<typeof InviteSchema>;

export function CreateInviteForm({ onComplete }: { onComplete: () => void }) {
	const client = useQueryClient();
	const { mutate: createInvite } = useCreateInvite();
	const { data: groups } = useGetGroups();
	const [selectedGroupNames, setSelectedGroupNames] = useState<string[]>([]);
	const [inviteType, setInviteType] = useState<"internal" | "external">(
		"internal",
	);

	const {
		handleSubmit,
		formState: { errors },
	} = useForm<InviteFormType>({
		defaultValues: {
			groups: [],
			type: "internal",
		},
		resolver: zodResolver(InviteSchema),
		reValidateMode: "onSubmit",
	});

	const toggleGroup = (groupName: string) => {
		setSelectedGroupNames((prev) =>
			prev.includes(groupName)
				? prev.filter((name) => name !== groupName)
				: [...prev, groupName],
		);
	};

	const onSubmit = () => {
		createInvite(
			{
				groups: selectedGroupNames.length > 0 ? selectedGroupNames : null,
				type: inviteType,
			},
			{
				onError: (err) => {
					toast(err.message || "Failed to create invitation");
				},
				onSuccess: () => {
					toast("Invitation created");
					client.invalidateQueries({ queryKey: ["invites"] });
					onComplete();
				},
			},
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
			<div className="flex flex-col gap-2">
				<Label>Invitation Type</Label>
				<Select
					value={inviteType}
					onValueChange={(value) =>
						setInviteType(value as "internal" | "external")
					}
				>
					<SelectTrigger className="min-w-[280px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="internal">Internal</SelectItem>
						<SelectItem value="external">External</SelectItem>
					</SelectContent>
				</Select>
				{errors.type && (
					<p className="text-xs text-red-600">{errors.type.message}</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label>Groups (Optional)</Label>
				<div className="flex flex-col gap-2 max-h-64 overflow-y-auto border border-input rounded-none p-2">
					{groups && groups.length > 0 ? (
						groups.map((group) => (
							<label
								key={group.id}
								className="flex items-center gap-2 cursor-pointer hover:bg-accent p-2 rounded-none"
							>
								<input
									type="checkbox"
									checked={selectedGroupNames.includes(group.group_name)}
									onChange={() => toggleGroup(group.group_name)}
									className="h-4 w-4 cursor-pointer"
								/>
								<span className="text-xs">
									{group.group_name} ({group.abbreviation})
								</span>
							</label>
						))
					) : (
						<p className="text-xs text-muted-foreground">No groups available</p>
					)}
				</div>
				{errors.groups && (
					<p className="text-xs text-red-600">{errors.groups.message}</p>
				)}
			</div>
			<div className="flex justify-end">
				<Button type="submit">Create Invite</Button>
			</div>
		</form>
	);
}
